import { useContext, useMemo, useState } from 'react';
import cloneDeep from 'lodash/cloneDeep';
import get from 'lodash/get';
import set from 'lodash/set';

import { ThreadModuleContext } from 'src/presentation/components/pages/sidekick/threads/components/SidePanel/ThreadModuleContext';
import FormContext from 'src/presentation/contexts/FormContext';
import { ThenTypes } from 'src/presentation/components/pages/sidekick/threads/components/SidePanel/Components/Rule/Then/ThenConstants';
import SidekickModuleSpec, { VCustomerIntegration } from 'src/models/Sidekick/SidekickModuleSpec';
import { createNewModule } from 'src/presentation/components/pages/sidekick/threads/actions/utils';
import { NextData } from 'src/models/Sidekick/SidekickThreads';
import { useResourceValues } from 'src/presentation/hooks/useResource';
import sidekickIntegrationsResource from 'src/actions/sidekick/sidekickIntegrationsResource';

const useChangeThen = (attr: string) => {
  const { workingState, onBlur } = useContext(FormContext);
  const { moduleSpecsMap } = useContext(ThreadModuleContext);
  const { values: sidekickIntegrations } = useResourceValues(sidekickIntegrationsResource);

  const tp = useMemo(() => {
    const type = get(workingState, attr, 'pivotFlowID') as Object;

    if (type) {
      const t = Object.keys(type);
      if (!t.includes('includeItems') || t.includes('omitItems')) {
        return t.includes('pivotFlowID') ? ThenTypes.pivotFlowID : ThenTypes.anonymousModule;
      }
    }

    return ThenTypes.hidden;
  }, [attr, workingState]);

  const [thenType, setThenType] = useState<ThenTypes>(tp);

  const actionOptions = useMemo(() => {
    return Object.keys(moduleSpecsMap)
      .filter(key => key !== 'rules')
      .map(key => ({
        label: moduleSpecsMap[key].presentableName || key,
        value: key,
      }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [moduleSpecsMap]) as { label: string; value: string }[];

  const pivotChange = (value: string) => {
    let clonedState = cloneDeep(workingState);
    clonedState = set(clonedState, `${attr}.pivotFlowID`, value);
    onBlur && onBlur({ currentState: clonedState });
  };

  const handleSwitch = (value: string) => {
    setThenType(value as ThenTypes);

    const initState = getRuleSetThenInitialState(
      value as ThenTypes,
      actionOptions[0]?.value,
      null,
      {
        specs: moduleSpecsMap,
      },
      sidekickIntegrations
    );

    let clonedState = cloneDeep(workingState);
    clonedState = set(clonedState, attr, initState);
    onBlur && onBlur({ currentState: clonedState, needToOverrideState: true });
  };

  const actionChange = (actionName: string) => {
    const initState = getRuleSetThenInitialState(
      ThenTypes.anonymousModule,
      actionName,
      '',
      {
        specs: moduleSpecsMap,
      },
      sidekickIntegrations
    );

    let clonedState = cloneDeep(workingState);
    clonedState = set(clonedState, attr, initState);
    onBlur && onBlur({ currentState: clonedState, needToOverrideState: true });
  };

  return { actionOptions, actionChange, handleSwitch, thenType, pivotChange };
};

const getRuleSetThenInitialState = (
  type: ThenTypes,
  actionName: string,
  threadId: string | null | undefined,
  options: { specs: Record<string, SidekickModuleSpec> },
  sidekickIntegrations: Record<string, VCustomerIntegration[]>
) => {
  switch (type) {
    case ThenTypes.anonymousModule:
      const newModule = createNewModule(actionName, options.specs, sidekickIntegrations) as Partial<NextData>;

      return { anonymousModule: newModule };
    case ThenTypes.pivotFlowID:
      return threadId ? { pivotFlowID: threadId } : { pivotFlowID: null };
    default:
      return {};
  }
};

export default useChangeThen;
